/**
 * DiceRollerModal.tsx
 *
 * Purpose: Modal wrapper for the DiceRoller component in SessionView.
 * Created: 2026-01-22
 * Modified: 2026-01-22
 *
 * WHO: Players during game sessions who want quick dice access.
 * WHAT: Displays the DiceRoller in a modal overlay for easy access.
 * WHERE: Mythbound.App/src/components/session/DiceRollerModal.tsx
 * WHY: Provides a focused dice rolling experience without leaving context.
 *
 * USAGE:
 * ```tsx
 * import { DiceRollerModal } from '../components/session/DiceRollerModal';
 *
 * const { isOpen, onOpen, onClose } = useDisclosure();
 *
 * <Button onClick={onOpen}>Roll Dice</Button>
 * <DiceRollerModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   sessionId={sessionId}
 *   playerScreenName={playerScreenName}
 *   characterName={characterName}
 * />
 * ```
 *
 * INTEGRATION POINTS:
 * - SessionView.tsx: Triggered by "Roll Dice" GameControl button
 * - DiceRoller.tsx: Wraps the existing DiceRoller component
 * - WebSocket: Dice rolls are broadcast via existing WS infrastructure
 */

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
} from '@chakra-ui/react';
import { theme as mythboundTheme } from '../../config/theme';
import { DiceRoller } from '../dice/DiceRoller';

// =============================================================================
// TYPES
// =============================================================================

export interface DiceRollerModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Current session ID for dice roll context */
  sessionId: string;
  /** Player's screen name for roll attribution */
  playerScreenName: string;
  /** Character name for roll attribution (optional) */
  characterName?: string;
}

// =============================================================================
// COMPONENT: DiceRollerModal
// =============================================================================

/**
 * DiceRollerModal Component
 *
 * WHO: Players in active game sessions.
 * WHAT: Displays the DiceRoller component in a focused modal view.
 * WHERE: SessionView page, triggered by "Roll Dice" button.
 * WHY: Allows quick dice access without cluttering the main game view.
 *
 * ASYNC INTEGRATION:
 * - DiceRoller handles all WebSocket communication
 * - Rolls are automatically broadcast to session participants
 * - History is maintained in the DiceRoller component
 */
export const DiceRollerModal: React.FC<DiceRollerModalProps> = ({
  isOpen,
  onClose,
  sessionId,
  playerScreenName,
  characterName,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="md"
      motionPreset="slideInBottom"
    >
      <ModalOverlay
        bg="rgba(0, 0, 0, 0.75)"
        backdropFilter="blur(4px)"
      />
      <ModalContent
        bg={mythboundTheme.colors.primary.dark}
        borderWidth={2}
        borderColor={mythboundTheme.colors.primary.main}
        borderRadius="lg"
        boxShadow={mythboundTheme.shadows.glow}
        mx={4}
        maxH="90vh"
        overflow="hidden"
      >
        {/* =================================================================
            Modal Header
        ================================================================= */}
        <ModalHeader
          fontFamily={mythboundTheme.typography.fontFamily.heading}
          color={mythboundTheme.colors.primary.gold}
          borderBottomWidth={1}
          borderBottomColor={mythboundTheme.colors.accent.brown}
          pb={3}
        >
          Dice Roller
        </ModalHeader>
        <ModalCloseButton color={mythboundTheme.colors.accent.silver} />

        {/* =================================================================
            Modal Body - Contains the DiceRoller component
        ================================================================= */}
        <ModalBody py={4} overflowY="auto">
          <Box>
            {/* ===============================================================
                INTEGRATION POINT: DiceRoller Component
                The DiceRoller component handles:
                - Dice notation input (1d20, 2d6+5, etc.)
                - Roll animations
                - WebSocket broadcasting
                - Roll history
            =============================================================== */}
            <DiceRoller
              sessionId={sessionId}
              playerScreenName={playerScreenName}
              characterName={characterName}
              showHistory={true}
              compact={false}
            />
          </Box>
        </ModalBody>

        {/* =================================================================
            Note: No footer needed - DiceRoller has its own roll button
        ================================================================= */}
      </ModalContent>
    </Modal>
  );
};

export default DiceRollerModal;
